<template lang="pug">
FetchErrorTransition(:isFetching="isFetching" :error="error" :parsedException="parsedException")
  EmailSettingsPage(:emailSettings="emailSettings" :placeholders="placeholders" @save="save" :isSaving="isSaving" :enableMultilanguage="enableMultilanguage")
</template>

<script>
import { mapState } from 'vuex'
import EmailSettingsPage from '@v/settings/application/emails/EmailSettingsPage.vue'
import tryCall from '@v/mixins/tryCall.js'
import { parseAxiosException } from '@/helpers/exceptionParser.js'

export default {
  components: { EmailSettingsPage },
  mixins: [tryCall],
  props: {
    enableMultilanguage: Boolean, // feature flag
  },
  data() {
    return {
      emailSettings: null,
      placeholders: null,
      savePending: false,
      isFetching: false,
      error: null,
      parsedException: null,
      isSaving: false,
    }
  },
  beforeMount() {
    this.getEmailSettingsData()
  },
  computed: {
    ...mapState('imagePicker', ['file', 'removeExistingFile']),
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage && !!this.emailSettings.TranslationInfo?.AvailableCultures?.length
      )
    },
  },
  methods: {
    async getEmailSettingsData() {
      this.isFetching = true
      try {
        const response = await this.$apiClient.get('settings/application/emails')
        this.emailSettings = response.data.item
        this.placeholders = response.data.placeholders
      } catch (e) {
        const message = 'There was a problem: ' + e.message
        this.$notifyError(message)
        const parsedException = parseAxiosException(e)
        this.$logError(`Error expected to originate from ${parsedException.origin}`)
        this.$logError(parsedException.data)
        this.error = e
        this.parsedException = parsedException
      }
      this.isFetching = false
    },
    async save() {
      this.isSaving = true
      const { emailSettings } = this
      const saveImageResult = await this.saveImage()
      const imageUrl = saveImageResult.data || emailSettings.EmailLogo
      const dataToSave = {
        PrimaryColor: emailSettings.PrimaryColor,
        SecondaryColor: emailSettings.SecondaryColor,
        EmailLogo: imageUrl,
        CustomMailText: emailSettings.CustomMailText,
        CustomReminderMailText: emailSettings.CustomReminderMailText,
        PurchaseSiteBaseUrl: emailSettings.PurchaseSiteBaseUrl,
        EmailCompanyUrl: emailSettings.EmailCompanyUrl,
        EmailPasswordResetTemplateName: emailSettings.EmailPasswordResetTemplateName,
        EmailPasswordResetUsersTemplateName: emailSettings.EmailPasswordResetUsersTemplateName,
        EmailPasswordCreateUsersTemplateName: emailSettings.EmailPasswordCreateUsersTemplateName,
        BookingCompletedMailTemplateName: emailSettings.BookingCompletedMailTemplateName,
        BookingCompletedOfflineMailTemplateName:
          emailSettings.BookingCompletedOfflineMailTemplateName,
        BookingCancelledMailTemplateName: emailSettings.BookingCancelledMailTemplateName,
        BookingPaidMailTemplateName: emailSettings.BookingPaidMailTemplateName,
        EvaluationMailTemplateName: emailSettings.EvaluationMailTemplateName,
        EmailFromAddress: emailSettings.EmailFromAddress,
        EmailReplyToEmailaddress: emailSettings.EmailReplyToEmailaddress,
        BookingConceptMailTemplateName: emailSettings.BookingConceptMailTemplateName,
        TranslationInfo: this.hasMultipleLanguages ? emailSettings.TranslationInfo : null,
      }
      if (this.removeExistingFile && !saveImageResult.data) {
        dataToSave.EmailLogo = null
      }
      const saveEmailsResult = await this.saveEmailsData(dataToSave)
      const success = saveImageResult.success && saveEmailsResult.success
      if (success) {
        this.$notifySuccess('Settings saved')
      }
      this.isSaving = false
    },
    async saveEmailsData(emailSettingsData) {
      return await this.tryCall(
        async () => await this.$apiClient.post('settings/application/emails', emailSettingsData),
        'Could not save settings!',
      )
    },
    async saveImage() {
      return this.tryCall(async () => {
        if (!this.file) return
        const formData = new FormData()
        formData.append('image', this.file)
        const response = await this.$apiClient.post(`//${window.location.host}/image`, formData, {
          headers: {
            accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
          },
        })
        return response.data.url
      }, 'Could not save image!')
    },
  },
}
</script>
